import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Zap, Heart, UserPlus, UserMinus, Eye, MessageCircle,
  Play, Square, Hash, Repeat2, Users, Target, ChevronDown, ChevronUp,
  Settings, Activity, AlertCircle, Scan, List, Smartphone,
  Plus, Trash2, CheckSquare
} from 'lucide-react';
import AutomationBanner from '../components/AutomationBanner';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const slideIn = keyframes`from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); }`;

const Page = styled.div`width: 100%;`;

const ContentGrid = styled.div`
  display: grid; grid-template-columns: 1fr 380px; gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; overflow: hidden;
`;

const CardHead = styled.div`
  padding: 14px 18px; border-bottom: 1px solid var(--border);
  display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 14px;
  color: var(--text-primary);
  svg { color: ${p => p.$color || '#1da1f2'}; }
`;

/* ── Account Info ── */
const AccountRow = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 14px 18px;
  border-bottom: 1px solid var(--border);
`;

const AccountPic = styled.div`
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #1da1f2, #0d8bd9);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 18px;
`;

const AccountInfo = styled.div`
  flex: 1;
  .name { font-weight: 700; font-size: 14px; color: var(--text-primary); }
  .handle { font-size: 12px; color: var(--text-muted); font-family: monospace; }
`;

const StatsGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 10px 18px 14px;
`;

const StatBadge = styled.div`
  display: flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 6px;
  background: ${p => p.$color}12; font-size: 11px; font-weight: 700;
  svg { color: ${p => p.$color}; }
  .val { color: ${p => p.$color}; font-family: 'JetBrains Mono', monospace; }
  .lbl { color: var(--text-muted); font-size: 9px; font-weight: 600; }
`;

/* ── Script Tabs ── */
const TabRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 14px 18px;
  border-bottom: 1px solid var(--border);
`;

const TabBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 6px 14px; border-radius: 8px; border: none; cursor: pointer;
  font-size: 12px; font-weight: 700; transition: all .15s;
  background: ${p => p.$act ? `${p.$color || '#1da1f2'}` : 'var(--surface)'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  &:hover { opacity: .9; transform: translateY(-1px); }
`;

const SubTabRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 18px 14px;
`;

const SubTab = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; border: 1px solid transparent; cursor: pointer;
  font-size: 11px; font-weight: 600; transition: all .15s;
  background: ${p => p.$act ? `${p.$color || '#1da1f2'}15` : 'transparent'};
  color: ${p => p.$act ? p.$color || '#1da1f2' : 'var(--text-muted)'};
  border-color: ${p => p.$act ? `${p.$color || '#1da1f2'}30` : 'transparent'};
  &:hover { color: ${p => p.$color || '#1da1f2'}; }
`;

/* ── Launch Button ── */
const LaunchBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 24px; border-radius: 10px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 700; color: #fff; transition: all .2s;
  background: ${p => p.$stop ? '#ef4444' : 'linear-gradient(135deg, #1da1f2, #0d8bd9)'};
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px ${p => p.$stop ? 'rgba(239,68,68,.3)' : 'rgba(29,161,242,.3)'}; }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; }
`;

/* ── Params ── */
const ParamsSection = styled.div`padding: 14px 18px;`;
const ParamRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 8px 0;
`;
const ParamLabel = styled.span`font-size: 12px; font-weight: 600; color: var(--text-primary);`;
const ParamValue = styled.span`font-size: 12px; font-weight: 700; color: #1da1f2; font-family: 'JetBrains Mono', monospace;`;

const ParamSlider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none; width: 100%; height: 4px; border-radius: 2px;
  background: var(--border); outline: none; margin-bottom: 12px;
  &::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%;
    background: #1da1f2; cursor: pointer; }
`;

const TargetInput = styled.input`
  flex: 1; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--input-bg, var(--surface));
  color: var(--text-primary); font-size: 12px; outline: none;
  &:focus { border-color: #1da1f2; }
`;

/* ── Target Card ── */
const TargetCard = styled.div`
  border-radius: 12px; background: var(--card-bg);
  border: 1px solid var(--card-border); overflow: hidden;
  transition: border-color .2s;
  &:hover { border-color: rgba(29,161,242,.3); }
`;

const TargetCardHeader = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px;
  cursor: pointer; user-select: none;
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px;
  &:hover { color: var(--text-secondary); }
`;

const TargetCount = styled.span`
  margin-left: auto; padding: 2px 8px; border-radius: 10px;
  font-size: 10px; font-weight: 700;
  background: ${p => p.$count > 0 ? 'rgba(29,161,242,.15)' : 'var(--surface)'};
  color: ${p => p.$count > 0 ? '#1da1f2' : 'var(--text-muted)'};
`;

const SelectAllBtn = styled.button`
  display: flex; align-items: center; gap: 4px; padding: 4px 10px; margin: 0 10px;
  border-radius: 6px; border: none; cursor: pointer; font-size: 10px; font-weight: 600;
  background: ${p => p.$active ? 'rgba(29,161,242,.12)' : 'var(--surface)'};
  color: ${p => p.$active ? '#1da1f2' : 'var(--text-muted)'};
  &:hover { color: #1da1f2; }
`;

const TargetTableHead = styled.div`
  display: grid; grid-template-columns: 28px 1fr 70px 70px 55px 30px; gap: 6px;
  padding: 4px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; color: var(--text-muted); border-bottom: 1px solid var(--border);
  position: sticky; top: 0; background: var(--card-bg); z-index: 1;
`;

const SortHeader = styled.span`
  cursor: pointer; display: flex; align-items: center; gap: 2px;
  &:hover { color: var(--text-primary); }
  ${p => p.$active && 'color: #1da1f2;'}
`;

const TargetList = styled.div`
  max-height: 220px; overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const TargetRow = styled.button`
  display: grid; grid-template-columns: 28px 1fr 70px 70px 55px 30px; gap: 6px;
  align-items: center; padding: 5px 10px; border: none; text-align: left; width: 100%;
  cursor: pointer; transition: background .1s; font-size: 12px;
  background: ${p => p.$selected ? 'rgba(29,161,242,.1)' : 'transparent'};
  color: ${p => p.$selected ? '#1da1f2' : 'var(--text-secondary)'};
  &:hover { background: var(--surface-hover, rgba(29,161,242,.06)); }
`;

const TargetCheckbox = styled.div`
  width: 16px; height: 16px; border-radius: 4px; display: flex;
  align-items: center; justify-content: center;
  border: 1.5px solid ${p => p.$checked ? '#1da1f2' : 'var(--border)'};
  background: ${p => p.$checked ? '#1da1f2' : 'transparent'};
  svg { color: #fff; }
`;

const TargetDeleteBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 4px; border: none;
  background: transparent; color: var(--text-muted); cursor: pointer;
  &:hover { background: rgba(239,68,68,.1); color: #ef4444; }
`;

const ManualInputRow = styled.div`
  display: flex; gap: 6px; align-items: center; padding: 8px 10px;
  border-top: 1px solid var(--border);
`;

const ManualAddBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 6px; border: none;
  background: #1da1f2; color: #fff; cursor: pointer; flex-shrink: 0;
  transition: all .15s;
  &:hover { opacity: .85; }
  &:disabled { opacity: .3; cursor: not-allowed; }
`;

/* ── Actions Feed ── */
const ActionsList = styled.div`
  max-height: 500px; overflow-y: auto; padding: 0 14px 14px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ActionItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 8px 6px;
  border-bottom: 1px solid var(--border);
  animation: ${slideIn} .3s ease;
  &:last-child { border-bottom: none; }
`;

const ActionDot = styled.div`
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  background: ${p => p.$color}15; display: flex; align-items: center; justify-content: center;
  svg { color: ${p => p.$color}; }
`;

const ActionText = styled.div`
  flex: 1; min-width: 0;
  .user { font-size: 12px; font-weight: 700; color: var(--text-primary);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .type { font-size: 10px; color: var(--text-muted); }
`;

const ActionTime = styled.span`font-size: 10px; color: var(--text-muted); white-space: nowrap;`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px; color: var(--text-muted);
  svg { margin-bottom: 8px; opacity: .4; }
  span { font-size: 13px; }
`;

/* ═══════════════════════════════════════════════
   ACTION TYPE HELPERS
   ═══════════════════════════════════════════════ */

const ACTION_META = {
  LikeAction:        { icon: Heart, color: '#ef4444', label: 'Like' },
  RetweetAction:     { icon: Repeat2, color: '#22c55e', label: 'Retweet' },
  FollowAction:      { icon: UserPlus, color: '#1da1f2', label: 'Follow' },
  UnfollowAction:    { icon: UserMinus, color: '#f59e0b', label: 'Unfollow' },
  ViewProfileAction: { icon: Eye, color: '#3b82f6', label: 'Profil' },
  ReplyAction:       { icon: MessageCircle, color: '#a855f7', label: 'Reply' },
  ScrapeAction:      { icon: Scan, color: '#06b6d4', label: 'Scrape' },
};

const getActionMeta = (type) => ACTION_META[type] || { icon: Activity, color: '#6b7280', label: type };

const fmtDuration = (start) => {
  const ms = Date.now() - new Date(start).getTime();
  const s = Math.floor(ms / 1000); const m = Math.floor(s / 60); const h = Math.floor(m / 60);
  return `${String(h).padStart(2,'0')}:${String(m%60).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
};

const fmtAgo = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  return `${Math.floor(s/3600)}h`;
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const TwitterAutomation = ({ selectedDevice, onNavigate }) => {
  const deviceId = selectedDevice?.device_id;

  // State
  const [account, setAccount] = useState(null);
  const [actions, setActions] = useState([]);
  const [todaySessions, setTodaySessions] = useState([]);
  const [processInfo, setProcessInfo] = useState(null);
  const [timer, setTimer] = useState('00:00:00');
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);

  // Script params
  const [scriptType, setScriptType] = useState('interact');
  const [interactionType, setInteractionType] = useState('followers');
  const [targetUsername, setTargetUsername] = useState('');
  const [likesPerProfile, setLikesPerProfile] = useState(3);
  const [followPct, setFollowPct] = useState(30);
  const [retweetPct, setRetweetPct] = useState(10);
  const [sessionMaxMins, setSessionMaxMins] = useState(120);
  const [openTerminal, setOpenTerminal] = useState(true);
  const [paramsOpen, setParamsOpen] = useState(true);

  // Target selector state
  const [allTargets, setAllTargets] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState(new Set());
  const [manualInput, setManualInput] = useState('');
  const [targetsExpanded, setTargetsExpanded] = useState(true);
  const [targetSort, setTargetSort] = useState({ key: 'name', dir: 'asc' });

  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedAtRef = useRef(0);

  // ── Data Fetching ──

  const fetchAccount = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await fetch(`/api/twitter-scripts/account/${deviceId}`);
      const d = await r.json();
      setAccount(d.account || null);
    } catch (_) {}
  }, [deviceId]);

  const fetchActions = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await fetch(`/api/twitter-scripts/live-actions/${account.id}?limit=80`);
      const d = await r.json();
      setActions(d.actions || []);
    } catch (_) {}
  }, [account?.id]);

  const fetchProcess = useCallback(async () => {
    if (!account?.id) return;
    if (Date.now() - stoppedAtRef.current < 10000) return;
    try {
      const r = await fetch(`/api/twitter-scripts/status/${account.id}`);
      const d = await r.json();
      setProcessInfo(d.isRunning ? d : null);
    } catch (_) { setProcessInfo(null); }
  }, [account?.id]);

  const fetchTodaySessions = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await fetch(`/api/twitter-scripts/today-sessions/${account.id}`);
      const d = await r.json();
      setTodaySessions(d.sessions || []);
    } catch (_) {}
  }, [account?.id]);

  const fetchTargets = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await fetch(`/api/twitter-scripts/targets/${account.id}?purpose=interact`);
      const d = await r.json();
      const tgts = (d.targets || []).filter(t => t.status === 'active' || !t.status);
      setAllTargets(tgts);
      setSelectedTargets(new Set(tgts.map(t => t.target_profile_name)));
    } catch (_) {}
  }, [account?.id]);

  // Initial load + polling
  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  useEffect(() => {
    if (!account?.id) return;
    fetchActions(); fetchProcess(); fetchTodaySessions(); fetchTargets();
    pollRef.current = setInterval(() => { fetchActions(); fetchProcess(); fetchTodaySessions(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [account?.id, fetchActions, fetchProcess, fetchTodaySessions, fetchTargets]);

  // Timer
  useEffect(() => {
    const start = processInfo?.startedAt || processInfo?.started_at;
    if (processInfo?.isRunning && start) {
      const tick = () => setTimer(fmtDuration(start));
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else { setTimer('00:00:00'); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [processInfo]);

  // ── Target Management ──

  const toggleTarget = (name) => {
    setSelectedTargets(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const allSelected = allTargets.length > 0 && selectedTargets.size === allTargets.length;
  const totalSelectedCount = selectedTargets.size;

  const toggleAll = () => {
    if (allSelected) setSelectedTargets(new Set());
    else setSelectedTargets(new Set(allTargets.map(t => t.target_profile_name)));
  };

  const addTargetToDB = async () => {
    const raw = manualInput.trim();
    const val = raw.replace(/^@/, '');
    if (!val || !account?.id) return;
    try {
      const r = await fetch('/api/twitter-scripts/targets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: account.id, username: val, type: 'user', purpose: 'interact' }),
      });
      const d = await r.json();
      if (d.success) {
        setManualInput('');
        fetchTargets();
      } else {
        setLaunchError(d.error || 'Erreur');
        setTimeout(() => setLaunchError(null), 3000);
      }
    } catch (e) { setLaunchError('Erreur réseau'); setTimeout(() => setLaunchError(null), 3000); }
  };

  const deleteTarget = async (targetId) => {
    try {
      await fetch(`/api/twitter-scripts/targets/${targetId}`, { method: 'DELETE' });
      fetchTargets();
    } catch (_) {}
  };

  const toggleTargetSort = (key) => {
    setTargetSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' });
  };

  const sortedTargets = [...allTargets].sort((a, b) => {
    const dir = targetSort.dir === 'asc' ? 1 : -1;
    switch (targetSort.key) {
      case 'interactions': return ((a.total_interactions || 0) - (b.total_interactions || 0)) * dir;
      case 'users': return ((a.unique_users || 0) - (b.unique_users || 0)) * dir;
      default: return (a.target_profile_name || '').localeCompare(b.target_profile_name || '') * dir;
    }
  });

  // ── Computed helpers ──
  const needsTarget = scriptType === 'interact' && (interactionType === 'followers' || interactionType === 'following');
  const hasTarget = targetUsername.trim().length > 0 || (needsTarget && selectedTargets.size > 0);
  const canLaunch = !launching && !!deviceId && (
    scriptType === 'interact' && interactionType === 'feed' ? true :
    scriptType === 'unfollow' ? true :
    hasTarget
  );

  // ── Launch / Stop ──

  const handleLaunch = async () => {
    if (launching || !deviceId) return;
    if (!canLaunch) return;
    setLaunching(true); setLaunchError(null);
    try {
      // Build target string: selected targets joined by comma, or manual input
      let targetStr = targetUsername.trim().replace(/^@/, '');
      if (needsTarget && selectedTargets.size > 0) {
        targetStr = Array.from(selectedTargets).join(',');
      }

      const body = {
        device_id: deviceId,
        script_type: scriptType,
        interaction_type: interactionType,
        target_username: targetStr,
        username: account?.twitter_handle || '',
        likes_per_profile: likesPerProfile,
        follow_percentage: followPct,
        retweet_percentage: retweetPct,
        session_max_mins: sessionMaxMins,
        open_terminal: openTerminal,
      };
      if (needsTarget && selectedTargets.size > 0 && selectedTargets.size < allTargets.length) {
        body.selected_targets = Array.from(selectedTargets);
      }
      const r = await fetch('/api/twitter-scripts/start-interaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        setLaunchError(data.error || `Erreur ${r.status}`);
      } else {
        setProcessInfo({
          isRunning: true, scriptType,
          startedAt: new Date().toISOString(),
        });
        setTimeout(() => { fetchProcess(); }, 5000);
      }
    } catch (e) { setLaunchError(e.message); }
    setLaunching(false);
  };

  const handleStop = async () => {
    stoppedAtRef.current = Date.now();
    setProcessInfo(null);
    try {
      await fetch('/api/twitter-scripts/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId }),
      });
    } catch (_) {}
  };

  // ── Computed ──
  const isRunning = processInfo?.isRunning;
  const handle = account?.twitter_handle || '';

  // Today's session stats aggregated
  const sessionCounts = (() => {
    const totals = { likes: 0, follows: 0, retweets: 0, profiles: 0, unfollows: 0, replies: 0 };
    todaySessions.forEach(s => {
      totals.likes += parseInt(s.total_likes) || 0;
      totals.follows += parseInt(s.total_follows) || 0;
      totals.retweets += parseInt(s.total_retweets) || 0;
      totals.unfollows += parseInt(s.total_unfollows) || 0;
      totals.replies += parseInt(s.total_dms_sent) || 0;
    });
    // Fallback: count from actions if no session data
    if (Object.values(totals).every(v => v === 0) && actions.length > 0) {
      actions.forEach(a => {
        if (a.type === 'LikeAction') totals.likes++;
        else if (a.type === 'FollowAction') totals.follows++;
        else if (a.type === 'RetweetAction') totals.retweets++;
        else if (a.type === 'ViewProfileAction') totals.profiles++;
        else if (a.type === 'UnfollowAction') totals.unfollows++;
        else if (a.type === 'ReplyAction') totals.replies++;
      });
    }
    return totals;
  })();

  // ── Render ──
  if (!deviceId) return <Page><EmptyState><Smartphone size={32} /><span>Sélectionnez un appareil</span></EmptyState></Page>;

  return (
    <Page>
      {/* ── Top Banner ── */}
      <AutomationBanner
        platform="twitter"
        deviceName={deviceId}
        isConnected={!isRunning}
        isRunning={isRunning}
        timer={isRunning ? timer : null}
        onNavigate={onNavigate}
      />

      <ContentGrid>
        {/* ═══════ LEFT COLUMN ═══════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Account Info Card */}
          <Card>
            <AccountRow>
              <AccountPic>{(handle[0] || '?').toUpperCase()}</AccountPic>
              <AccountInfo>
                <div className="name">@{handle || '—'}</div>
                <div className="handle">{deviceId}</div>
              </AccountInfo>
            </AccountRow>

            {/* Session Stats Badges */}
            <StatsGrid>
              {[
                { icon: Heart, color: '#ef4444', val: sessionCounts.likes, label: 'LIKES' },
                { icon: UserPlus, color: '#1da1f2', val: sessionCounts.follows, label: 'FOLLOWS' },
                { icon: Repeat2, color: '#22c55e', val: sessionCounts.retweets, label: 'RETWEETS' },
                { icon: Eye, color: '#3b82f6', val: sessionCounts.profiles, label: 'PROFILS' },
                { icon: UserMinus, color: '#f59e0b', val: sessionCounts.unfollows, label: 'UNFOLLOWS' },
                { icon: MessageCircle, color: '#a855f7', val: sessionCounts.replies, label: 'REPLIES' },
              ].filter(s => s.val > 0).map(s => (
                <StatBadge key={s.label} $color={s.color}>
                  <s.icon size={10} />
                  <span className="val">{s.val}</span>
                  <span className="lbl">{s.label}</span>
                </StatBadge>
              ))}
            </StatsGrid>

            {/* Script Type Tabs */}
            <TabRow>
              {[
                { id: 'interact', label: 'Interact', icon: Zap, color: '#1da1f2' },
                { id: 'scrape', label: 'Scrape', icon: Scan, color: '#3b82f6' },
                { id: 'unfollow', label: 'Unfollow', icon: UserMinus, color: '#f59e0b' },
                { id: 'dm', label: 'DM', icon: MessageCircle, color: '#a855f7' },
              ].map(t => (
                <TabBtn key={t.id} $act={scriptType === t.id} $color={t.color}
                  onClick={() => setScriptType(t.id)}>
                  <t.icon size={13} /> {t.label}
                </TabBtn>
              ))}
            </TabRow>

            {/* Interaction Sub-Tabs (only for interact) */}
            {scriptType === 'interact' && (
              <SubTabRow>
                {[
                  { id: 'followers', label: 'Followers', icon: Users, color: '#22c55e' },
                  { id: 'following', label: 'Following', icon: UserPlus, color: '#3b82f6' },
                  { id: 'feed', label: 'Feed', icon: List, color: '#1da1f2' },
                  { id: 'hashtag', label: 'Hashtag', icon: Hash, color: '#a855f7' },
                ].map(t => (
                  <SubTab key={t.id} $act={interactionType === t.id} $color={t.color}
                    onClick={() => setInteractionType(t.id)}>
                    <t.icon size={12} /> {t.label}
                  </SubTab>
                ))}
              </SubTabRow>
            )}

            {/* Target Input + Launch */}
            <div style={{ padding: '0 18px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              {scriptType === 'interact' && interactionType !== 'feed' && (
                <TargetInput
                  placeholder={interactionType === 'hashtag' ? '#hashtag' : '@username cible'}
                  value={targetUsername}
                  onChange={e => setTargetUsername(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && canLaunch) handleLaunch(); }}
                />
              )}
              {scriptType === 'scrape' && (
                <TargetInput placeholder="@username à scraper" value={targetUsername}
                  onChange={e => setTargetUsername(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && canLaunch) handleLaunch(); }} />
              )}
              {scriptType === 'dm' && (
                <TargetInput placeholder="@username" value={targetUsername}
                  onChange={e => setTargetUsername(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && canLaunch) handleLaunch(); }} />
              )}
              {!isRunning ? (
                <LaunchBtn onClick={handleLaunch} disabled={!canLaunch}>
                  <Play size={14} /> {launching ? 'Lancement…' : 'Lancer'}
                </LaunchBtn>
              ) : (
                <LaunchBtn $stop onClick={handleStop}>
                  <Square size={14} /> Arrêter
                </LaunchBtn>
              )}
            </div>

            {/* Terminal checkbox */}
            <div style={{ padding: '0 18px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={openTerminal} onChange={e => setOpenTerminal(e.target.checked)} />
              <span style={{ color: 'var(--text-muted)' }}>Ouvrir un terminal pour les logs du script</span>
            </div>

            {launchError && (
              <div style={{ padding: '0 18px 14px', fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={14} /> {launchError}
              </div>
            )}
          </Card>

          {/* ── Target Selector (for followers/following interact) ── */}
          {scriptType === 'interact' && (interactionType === 'followers' || interactionType === 'following') && (
            <TargetCard>
              <TargetCardHeader onClick={() => setTargetsExpanded(p => !p)}>
                <Target size={13} />
                Cibles d'interaction
                <TargetCount $count={totalSelectedCount}>
                  {totalSelectedCount} sélectionnée{totalSelectedCount !== 1 ? 's' : ''}
                </TargetCount>
                {targetsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </TargetCardHeader>

              {targetsExpanded && (
                <>
                  {allTargets.length > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px 4px' }}>
                        <SelectAllBtn $active={allSelected} onClick={toggleAll}>
                          {allSelected ? <CheckSquare size={12} /> : <Square size={12} />}
                          {allSelected ? 'Désélectionner tout' : 'Tout sélectionner'}
                        </SelectAllBtn>
                      </div>

                      <TargetTableHead>
                        <span></span>
                        <SortHeader $active={targetSort.key === 'name'} onClick={() => toggleTargetSort('name')}>
                          Cible {targetSort.key === 'name' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <SortHeader $active={targetSort.key === 'interactions'} onClick={() => toggleTargetSort('interactions')} style={{ justifyContent: 'flex-end' }}>
                          Actions {targetSort.key === 'interactions' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <SortHeader $active={targetSort.key === 'users'} onClick={() => toggleTargetSort('users')} style={{ justifyContent: 'flex-end' }}>
                          Users {targetSort.key === 'users' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                        </SortHeader>
                        <span style={{ textAlign: 'right' }}>Poids</span>
                        <span></span>
                      </TargetTableHead>

                      <TargetList>
                        {sortedTargets.map(t => (
                          <TargetRow
                            key={t.id || t.target_profile_name}
                            $selected={selectedTargets.has(t.target_profile_name)}
                            onClick={() => toggleTarget(t.target_profile_name)}
                          >
                            <TargetCheckbox $checked={selectedTargets.has(t.target_profile_name)}>
                              {selectedTargets.has(t.target_profile_name) && <CheckSquare size={10} />}
                            </TargetCheckbox>
                            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              @{t.target_profile_name}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                              {t.total_interactions || 0}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                              {t.unique_users || 0}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                              {t.target_weight || 1}
                            </span>
                            <TargetDeleteBtn onClick={(e) => { e.stopPropagation(); deleteTarget(t.id); }}>
                              <Trash2 size={11} />
                            </TargetDeleteBtn>
                          </TargetRow>
                        ))}
                      </TargetList>
                    </>
                  )}

                  <ManualInputRow>
                    <TargetInput
                      placeholder="@username cible"
                      value={manualInput}
                      onChange={e => setManualInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addTargetToDB(); }}
                    />
                    <ManualAddBtn onClick={addTargetToDB} disabled={!manualInput.trim()}>
                      <Plus size={14} />
                    </ManualAddBtn>
                  </ManualInputRow>
                </>
              )}
            </TargetCard>
          )}

          {/* ── Parameters ── */}
          <Card>
            <CardHead $color="#1da1f2" style={{ cursor: 'pointer' }} onClick={() => setParamsOpen(!paramsOpen)}>
              <Settings size={16} /> PARAMÈTRES {scriptType.toUpperCase()}
              <span style={{ marginLeft: 'auto' }}>
                {paramsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </CardHead>
            {paramsOpen && (
              <ParamsSection>
                <ParamRow>
                  <ParamLabel>Likes par profil</ParamLabel>
                  <ParamValue>{likesPerProfile}</ParamValue>
                </ParamRow>
                <ParamSlider min={0} max={10} value={likesPerProfile} onChange={e => setLikesPerProfile(+e.target.value)} />

                <ParamRow>
                  <ParamLabel>Follow %</ParamLabel>
                  <ParamValue>{followPct}%</ParamValue>
                </ParamRow>
                <ParamSlider min={0} max={100} value={followPct} onChange={e => setFollowPct(+e.target.value)} />

                <ParamRow>
                  <ParamLabel>Retweet %</ParamLabel>
                  <ParamValue>{retweetPct}%</ParamValue>
                </ParamRow>
                <ParamSlider min={0} max={100} value={retweetPct} onChange={e => setRetweetPct(+e.target.value)} />

                <ParamRow>
                  <ParamLabel>Durée session (min)</ParamLabel>
                  <ParamValue>{sessionMaxMins}</ParamValue>
                </ParamRow>
                <ParamSlider min={15} max={480} value={sessionMaxMins} onChange={e => setSessionMaxMins(+e.target.value)} />
              </ParamsSection>
            )}
          </Card>
        </div>

        {/* ═══════ RIGHT COLUMN — Dernières Actions ═══════ */}
        <Card>
          <CardHead $color="#1da1f2">
            <Zap size={16} /> Dernières actions
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
              {actions.length} événements
            </span>
          </CardHead>
          {actions.length === 0 ? (
            <EmptyState><Activity size={28} /><span>Aucune activité récente</span></EmptyState>
          ) : (
            <ActionsList>
              {actions.map((a, i) => {
                const meta = getActionMeta(a.type || a.action_type);
                const Icon = meta.icon;
                return (
                  <ActionItem key={a.id || i}>
                    <ActionDot $color={meta.color}><Icon size={13} /></ActionDot>
                    <ActionText>
                      <div className="user">{a.target_username || a.target_handle || '—'}</div>
                      <div className="type">{meta.label}</div>
                    </ActionText>
                    <ActionTime>{a.timestamp ? fmtAgo(a.timestamp) : ''}</ActionTime>
                  </ActionItem>
                );
              })}
            </ActionsList>
          )}
        </Card>
      </ContentGrid>
    </Page>
  );
};

export default TwitterAutomation;
